import { create } from "zustand";
import apis from "@/services/api";
import useGlobalStore from "./useGlobalStore";
import { getChatHistory } from "@/apis";
import { streamAskQuestionPOST } from "@/services/streamingApi";

//TODO: Clean structure of store
const useChatStore = create((set, get) => ({
  question: "",
  conversation: [],
  chatHistory: [],
  suggestedQuestions: [],
  conversationId: null,
  isFetchingConversation: false,
  loading: false,
  isAnswering: false,
  hasDisplayedAnswer: false,
  footerHeight: 64, // Default footer height
  referenceMessage: "",
  isThinking: false,
  reminderId: null,

  // Actions
  setQuestion: (value) => set({ question: value }),
  setFooterHeight: (height) => set({ footerHeight: height }),
  setHasDisplayedAnswer: (value) => set({ hasDisplayedAnswer: value }),
  resetConversation: () => set({ conversation: [], hasDisplayedAnswer: false, conversationId: null }),
  addUserMessage: (message) =>
    set((state) => ({
      conversation: [
        ...state.conversation,
        {
          role: "user",
          content: message,
          timestamp: new Date().toISOString(),
        },
      ],
    })),
  setReferenceMessage: (value) => set({ referenceMessage: value }),
  // Feedback handling
  submitFeedback: async (messageId, feedbackType) => {
    const { conversationId, conversation } = get();
    const message = conversation[messageId];

    if (!message || (message.role !== "ai" && message.role !== "assistant") || !conversationId) return;

    // Convert feedback type to rating
    const rating = feedbackType === "positive" ? "LIKE" : "DISLIKE";

    // Update the conversation message with rating
    set((state) => {
      const updatedConversation = [...state.conversation];
      updatedConversation[messageId] = {
        ...updatedConversation[messageId],
        rating,
      };
      return { conversation: updatedConversation };
    });

    try {
      // Send feedback to API
      await apis.post("/question/rate", {
        chatId: conversationId,
        messageId: message.id?.toString() || messageId.toString(),
        rating,
      });
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  },

  loadConversationById: async (chatId) => {
    try {
      set({ isFetchingConversation: true });

      const response = await apis.get(`/question/message_histories?chatId=${chatId}`);

      if (response && response.code === 0) {
        const messages = (response.data.messages || []).map((message, index) => {
          return {
            ...message,
            timestamp: message.timestamp || message.createdAt || new Date().toISOString(),
            // Api trả về nó lại nằm trong user nên phải lấy từ user trước đó map vào assistant
            thinkingMessages: response.data.messages[index - 1]?.step || [],
          };
        });

        set({
          conversation: messages,
          conversationId: chatId,
          loading: false,
          hasDisplayedAnswer: true,
          isFetchingConversation: false,
          reminderId: response.data.reminderId || null,
        });
      } else {
        set({
          conversation: [{ role: "ai", content: "Không thể tải cuộc hội thoại", timestamp: new Date().toISOString() }],
          loading: false,
          conversationId: null,
          isFetchingConversation: false,
        });
      }
    } catch (error) {
      console.error("Failed to load conversation:", error);
      set({
        conversation: [{ role: "ai", content: "Đã xảy ra lỗi khi tải cuộc hội thoại", timestamp: new Date().toISOString() }],
        loading: false,
        conversationId: null,
        isFetchingConversation: false,
      });
    }
  },

  loadChatHistory: async () => {
    const response = await getChatHistory();
    set({ chatHistory: response?.chats || [] });
  },

  handleAskQuestion: async () => {
    const { conversation, question, conversationId, handleFetchSuggestedQuestions, loadChatHistory } = get();
    const aiModel = useGlobalStore.getState().aiModel;

    set((state) => ({
      loading: true,
      isAnswering: true,
      hasDisplayedAnswer: false,
      conversation: [...state.conversation, { role: "user", content: question, timestamp: new Date().toISOString() }],
    }));

    try {
      let message = "";
      let suggestedQuestions = [];
      const data = { question };

      // Only add model if it exists
      if (aiModel) {
        data.model = aiModel;
      }

      if (conversationId) {
        data.chatId = conversationId;
      }

      const response = await apis.post("/question/ask", data);

      if (response.code === 0) {
        message = response.data.answer;
        handleFetchSuggestedQuestions(response.data.chatId);

        if (conversation?.length === 0) {
          //Wait for AI generate title
          setTimeout(() => {
            loadChatHistory();
          }, 1000);
        }
      } else {
        message = "Có lỗi xảy ra, vui lòng thử lại!";
      }

      set((state) => ({
        conversation: [...state.conversation, { role: "ai", content: message, timestamp: new Date().toISOString() }],
        loading: false,
        isAnswering: false,
        conversationId: response.data?.chatId || state.conversationId,
        suggestedQuestions,
      }));
    } catch (error) {
      set({ isAnswering: false });
      setTimeout(() => {
        set((state) => ({
          conversation: [...state.conversation, { role: "ai", content: error.message, timestamp: new Date().toISOString() }],
          loading: false,
          suggestedQuestions: [],
        }));
      }, 300);
    }
  },

  handleAskQuestionStream: async (reminderId, isSuggestion) => {
    let message = "";
    let suggestedQuestions = [];
    const { conversation, question, conversationId, handleFetchSuggestedQuestions, loadChatHistory } = get();
    const aiModel = useGlobalStore.getState().aiModel;

    // Add user message and prepare for streaming
    set((state) => ({
      loading: true,
      isAnswering: true,
      isThinking: true,
      hasDisplayedAnswer: false,
      suggestedQuestions: [],
      conversation: [...state.conversation, { role: "user", content: question, createTime: new Date().toISOString() }],
      reminderId,
    }));

    const aiMessageIndex = get().conversation.length;

    // Add empty AI message placeholder
    set((state) => ({
      conversation: [...state.conversation, { role: "ai", content: "", createTime: new Date().toISOString() }],
    }));

    try {
      const data = { question };

      // Only add model if it exists
      if (aiModel) {
        data.model = aiModel;
      }

      if (conversationId) {
        data.chatId = conversationId;
      }
      if (reminderId) {
        data.reminderId = reminderId;
      }
      if (isSuggestion) {
        data.isSuggestion = true;
      }

      await streamAskQuestionPOST(
        data,
        // onMessage callback - update streaming content
        ({ isThinking, thinkingMessage }) => {
          set((state) => {
            const updatedConversation = [...state.conversation];
            const updatedMessage = updatedConversation[aiMessageIndex];
            const updatedThinkingMessages = updatedMessage?.thinkingMessages || [];
            updatedMessage.thinkingMessages = [...updatedThinkingMessages, thinkingMessage];

            return {
              conversation: updatedConversation,
              isThinking: isThinking || false,
              hasDisplayedAnswer: false,
            };
          });
        },
        // onError callback
        (error) => {
          console.error("Streaming error:", error);
          set((state) => {
            const updatedConversation = [...state.conversation];
            const updatedMessage = updatedConversation[aiMessageIndex];
            updatedMessage.content = error.message || "Có lỗi xảy ra khi streaming!";

            return {
              conversation: updatedConversation,
              isAnswering: false,
              isThinking: false,
              hasDisplayedAnswer: true,
            };
          });
        },
        // onComplete callback
        (response) => {
          console.log("onComplete", response);
          if (response.code === 0) {
            message = response.data.answer;
            setTimeout(() => {
              handleFetchSuggestedQuestions(response.data.chatId);
            }, 2000);

            if (conversation?.length === 0) {
              //Wait for AI generate title
              setTimeout(() => {
                loadChatHistory();
              }, 1000);
            }
          } else {
            message = "Có lỗi xảy ra, vui lòng thử lại!";
          }

          set((state) => {
            const updatedConversation = [...state.conversation];
            const updatedMessage = updatedConversation[aiMessageIndex];
            updatedMessage.content = message;

            return {
              conversation: updatedConversation,
              loading: false,
              isAnswering: false,
              isThinking: false,
              conversationId: response.data?.chatId || state.conversationId,
              suggestedQuestions,
              hasDisplayedAnswer: false,
            };
          });
        }
      );
    } catch (error) {
      console.error("Streaming request failed:", error);
      set((state) => {
        const updatedConversation = [...state.conversation];
        if (updatedConversation[aiMessageIndex]) {
          updatedConversation[aiMessageIndex] = {
            ...updatedConversation[aiMessageIndex],
            content: error.message || "Có lỗi xảy ra khi streaming!",
            thinkingMessages: [...state.thinkingMessages], // Lưu thinking messages khi có lỗi
          };
        }
        return {
          conversation: updatedConversation,
          isAnswering: false,
          isThinking: false,
        };
      });
    }
  },

  handleFetchSuggestedQuestions: async (conversationId) => {
    const response = await apis.get(`/question/suggested_questions?chatId=${conversationId}`);
    set({ suggestedQuestions: response.data });
  },

  handleResetChatStore: () => {
    set({
      question: "",
      conversation: [],
      suggestedQuestions: [],
      conversationId: null,
      loading: false,
      isAnswering: false,
      hasDisplayedAnswer: false,
      reminderId: null,
    });
  },
}));

export default useChatStore;
